import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';
import i18next from 'i18next';
import { onQueryStartedErrorToast } from './apiErrorHandler';
import {
  CreateUserAreaRequest,
  CreateUserAreaResponse,
  DeleteUserAreaResponse,
  GetAreaCatalogResponse,
  GetPortsOnMapRequest,
  GetPortsOnMapResponse,
  PortCatalogFilterResponse,
  PortCatalogResponse,
  PortCatalogSearchRequest,
  SinglePortResponse,
  UpdateUserAreaRequest,
  UpdateUserAreaResponse,
} from 'ais-facade-api/api';

export interface ModifiedWeatherResponce {
  weather: {
    dt: number;
    id: number;
    cod: number;
    sys: {
      id: number;
      type: number;
      sunset: number;
      country: string;
      sunrise: number;
    };
    base: string;
    main: {
      temp: number;
      humidity: number;
      pressure: number;
      temp_max: number;
      temp_min: number;
      feels_like: number;
    };
    name: string;
    wind: {
      deg: number;
      gust: number;
      speed: number;
    };
    coord: {
      lat: number;
      lon: number;
    };
    clouds: {
      all: number;
    };
    weather: [
      {
        id: number;
        icon: string;
        main: string;
        description: string;
      },
    ];
    timezone: number;
    visibility: number;
  };
  full: boolean;
  utc?: any;
}
const BASE_URL = process.env?.NODE_ENV === 'development' ? window._env_.APP_DEV_API : window._env_.APP_API;

export const marineInfrastructureApi = createApi({
  reducerPath: 'marine-infrastructure',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL + '/facade/versions/1',
    credentials: 'include',
    mode: 'cors',
    prepareHeaders: (headers: Headers, { getState }) => {
      const userInfo = (getState() as RootState).userSlice;
      headers.set('Accept-Language', i18next.resolvedLanguage ? i18next.resolvedLanguage : 'ru');
      if (userInfo.auth && userInfo.user) {
        headers.set('x-ais-user-csrf', userInfo.auth.value.csrfToken);
        headers.set('ais-user-csrf', userInfo.auth.value.csrfToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getPorts: builder.mutation<GetPortsOnMapResponse, GetPortsOnMapRequest>({
      query: (data) => {
        return {
          url: 'port/map',
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body: data,
          cache: 'no-cache',
          overrideExisting: false,
          withCredentials: true,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPortCatalog: builder.mutation<PortCatalogResponse, PortCatalogSearchRequest>({
      query: (body) => {
        return {
          url: '/port/catalog',
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
          cache: 'no-cache',
          withCredentials: true,
          overrideExisting: false,
        };
      },
    }),

    getPortCatalogFilter: builder.query<PortCatalogFilterResponse, void>({
      query: () => ({
        url: `/filter/port/catalog`,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPort: builder.query<SinglePortResponse, string>({
      query: (key) => ({
        url: `/port/${key}`,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getWeatherByPortKey: builder.query<ModifiedWeatherResponce, string>({
      query: (key) => ({
        url: `/port/${key}/weather`,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),

    getAreas: builder.query<GetAreaCatalogResponse, void>({
      query: () => ({
        url: `/area-catalog`,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    createArea: builder.mutation<CreateUserAreaResponse, CreateUserAreaRequest>({
      query: (data) => {
        return {
          url: '/area/user',
          method: 'POST',
          body: data,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),

    deleteArea: builder.mutation<DeleteUserAreaResponse, string>({
      query: (code) => {
        return {
          url: `/area/user/${code}`,
          method: 'DELETE',
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    updateArea: builder.mutation<UpdateUserAreaResponse, { code: string; body: UpdateUserAreaRequest }>({
      query: ({ code, body }) => {
        return {
          url: `/area/user/${code}`,
          method: 'PUT',
          body,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
  }),
});

export const {
  useCreateAreaMutation,
  useDeleteAreaMutation,
  useGetPortCatalogFilterQuery,
  useGetPortCatalogMutation,
  useGetPortsMutation,
  useLazyGetAreasQuery,
  useLazyGetPortQuery,
  useLazyGetWeatherByPortKeyQuery,
  useUpdateAreaMutation,
} = marineInfrastructureApi;
